import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_MONITOR_LOAD_BY_ID,
    ACTION_MONITORS_LOAD_BY_MONITORING_ID,
    ACTION_MONITOR_LOAD_FAILURE,
    ACTION_MONITOR_LOAD_SUCCESS,

    ACTION_MONITOR_SAVE,
    ACTION_MONITOR_SAVE_SUCCESS,
    ACTION_MONITOR_SAVE_FAILURE,

    ACTION_MONITOR_CREATE,
    ACTION_MONITOR_CREATE_SUCCESS,
    ACTION_MONITOR_CREATE_FAILURE,

    ACTION_MONITOR_VALIDATE,
    ACTION_MONITOR_VALIDATE_SUCCESS,
    ACTION_MONITOR_VALIDATE_FAILURE,

    ACTION_MONITOR_CLEAR,
    ACTION_MONITOR_FILTER,

    MonitorAction,
} from '../actions/monitor';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IMonitorState {
    formData: Dtos.Monitor | Dtos.Monitor[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.Monitor[];
    formLength: number;
}

const initialState: IMonitorState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: IMonitorState, _isLoading: boolean): IMonitorState {
    const newState: IMonitorState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: IMonitorState,
    formData: Dtos.Monitor | Dtos.Monitor[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IMonitorState {
    const newState: IMonitorState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: IMonitorState, responseStatus: Dtos.ResponseStatus): IMonitorState {
    const newState: IMonitorState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: IMonitorState, formFilter: IFormFilter): IMonitorState {

    const items: Dtos.Monitor[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" ||
            p.personnel?.firstName?.toLowerCase()?.indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: IMonitorState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: IMonitorState, propertiesToClear?: string[]): IMonitorState {

    if (!propertiesToClear) {
        const newState: IMonitorState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IMonitorState, _isSaving: boolean): IMonitorState {
    const newState: IMonitorState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IMonitorState,
    formData: Dtos.Monitor | Dtos.Monitor[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IMonitorState {
    const newState: IMonitorState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IMonitorState, responseStatus: Dtos.ResponseStatus): IMonitorState {
    const newState: IMonitorState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IMonitorState, _isCreating: boolean): IMonitorState {
    const newState: IMonitorState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IMonitorState,
    formData: Dtos.Monitor,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IMonitorState {
    const newState: IMonitorState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IMonitorState, responseStatus: Dtos.ResponseStatus): IMonitorState {
    const newState: IMonitorState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: IMonitorState, _isValidating: boolean): IMonitorState {
    const newState: IMonitorState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: IMonitorState,
    formData: Dtos.Monitor,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IMonitorState {
    const newState: IMonitorState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: IMonitorState, responseStatus: Dtos.ResponseStatus): IMonitorState {
    const newState: IMonitorState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}


const MonitorReducer: Reducer<IMonitorState> = (state: IMonitorState = initialState, action: MonitorAction) => {
    switch (action.type) {
        case ACTION_MONITOR_LOAD_BY_ID:
        case ACTION_MONITORS_LOAD_BY_MONITORING_ID:
            return loading(state, true);
        case ACTION_MONITOR_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_MONITOR_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_MONITOR_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_MONITOR_SAVE:
            return saving(state, true);
        case ACTION_MONITOR_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_MONITOR_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_MONITOR_CREATE:
            return creating(state, true);
        case ACTION_MONITOR_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_MONITOR_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_MONITOR_VALIDATE:
            return validating(state, true);
        case ACTION_MONITOR_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_MONITOR_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_MONITOR_FILTER:
            return filter(state, action.formFilter);
    }

    return state;
}

export default MonitorReducer;